// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import {SettingsTabContainer, SettingsTabSection} from '@app/features/app/components/dialogs/shared/SettingsTabLayout';
import {PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import {
	getCachedDesktopTroubleshootingSettings,
	getDesktopTroubleshootingSettings,
	setDesktopDisableHardwareAcceleration,
} from '@app/features/devtools/utils/DesktopTroubleshootingUtils';
import {
	getAutostartStatus,
	getCachedAutostartStatus,
	setAutostartEnabled,
} from '@app/features/platform/utils/autostart';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {Switch} from '@app/features/ui/components/form/Switch';
import {
	getCachedDesktopWindowBehaviorSettings,
	getDesktopWindowBehaviorPendingRestart,
	getDesktopWindowBehaviorSettings,
	relaunchDesktopApp,
	setDesktopWindowBehaviorSettings,
} from '@app/features/ui/utils/DesktopWindowBehaviorUtils';
import {isDesktop, isNativeLinux, isNativeMacOS} from '@app/features/ui/utils/NativeUtils';
import * as UserSettingsCommands from '@app/features/user/commands/UserSettingsCommands';
import UserSettings from '@app/features/user/state/UserSettings';
import type {DesktopTroubleshootingSettings, DesktopWindowBehaviorSettings} from '@app/types/electron.d';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useLayoutEffect, useState} from 'react';

const AdvancedTab: React.FC = observer(() => {
	const {t} = useLingui();
	const {developerMode} = UserSettings;
	const cachedAutostart = getCachedAutostartStatus();
	const cachedWindowBehavior = getCachedDesktopWindowBehaviorSettings();
	const cachedTroubleshooting = getCachedDesktopTroubleshootingSettings();
	const [autostartEnabled, setAutostartEnabledState] = useState(cachedAutostart ?? false);
	const [autostartBusy, setAutostartBusy] = useState(cachedAutostart === null);
	const [desktopWindowBehavior, setDesktopWindowBehaviorState] = useState<DesktopWindowBehaviorSettings | null>(
		cachedWindowBehavior,
	);
	const [desktopWindowBehaviorBusy, setDesktopWindowBehaviorBusy] = useState(cachedWindowBehavior === null);
	const [desktopTroubleshooting, setDesktopTroubleshootingState] = useState<DesktopTroubleshootingSettings | null>(
		cachedTroubleshooting,
	);
	const [desktopTroubleshootingBusy, setDesktopTroubleshootingBusy] = useState(cachedTroubleshooting === null);
	const [trayChangePendingRestart, setTrayChangePendingRestart] = useState(false);
	useLayoutEffect(() => {
		let mounted = true;
		const initDesktopSettings = async () => {
			if (!isDesktop()) {
				if (mounted) {
					setAutostartBusy(false);
					setDesktopWindowBehaviorBusy(false);
					setDesktopTroubleshootingBusy(false);
				}
				return;
			}
			const needsAutostart = getCachedAutostartStatus() === null;
			const needsWindowBehavior = getCachedDesktopWindowBehaviorSettings() === null;
			const needsTroubleshooting = getCachedDesktopTroubleshootingSettings() === null;
			if (!needsAutostart && !needsWindowBehavior && !needsTroubleshooting) return;
			const [enabled, windowBehavior, troubleshooting] = await Promise.all([
				needsAutostart ? getAutostartStatus() : Promise.resolve(getCachedAutostartStatus()),
				needsWindowBehavior
					? getDesktopWindowBehaviorSettings()
					: Promise.resolve(getCachedDesktopWindowBehaviorSettings()),
				needsTroubleshooting
					? getDesktopTroubleshootingSettings()
					: Promise.resolve(getCachedDesktopTroubleshootingSettings()),
			]);
			if (!mounted) return;
			if (enabled !== null) {
				setAutostartEnabledState(enabled);
			}
			if (windowBehavior !== null) {
				setDesktopWindowBehaviorState(windowBehavior);
			}
			if (troubleshooting !== null) {
				setDesktopTroubleshootingState(troubleshooting);
			}
			setAutostartBusy(false);
			setDesktopWindowBehaviorBusy(false);
			setDesktopTroubleshootingBusy(false);
		};
		void initDesktopSettings();
		void getDesktopWindowBehaviorPendingRestart().then((pending) => {
			if (mounted) setTrayChangePendingRestart(pending);
		});
		return () => {
			mounted = false;
		};
	}, []);
	const handleHardwareAccelerationChange = useCallback(
		(value: boolean) => {
			const desiredDisableValue = !value;
			ModalCommands.push(
				modal(() => (
					<ConfirmModal
						title={value ? t`Enable Hardware Acceleration?` : t`Disable Hardware Acceleration?`}
						description={
							value ? (
								<Trans>
									Fluxer needs to restart for hardware acceleration to take effect. Any drafts you haven&rsquo;t sent
									will be lost.
								</Trans>
							) : (
								<Trans>
									Fluxer needs to restart to disable Chromium&rsquo;s hardware acceleration. Use this only if
									you&rsquo;re troubleshooting graphics glitches or high GPU usage. Any drafts you haven&rsquo;t sent
									will be lost.
								</Trans>
							)
						}
						primaryText={t`Restart Now`}
						primaryVariant="primary"
						secondaryText={t`Cancel`}
						onPrimary={async () => {
							setDesktopTroubleshootingBusy(true);
							const next = await setDesktopDisableHardwareAcceleration(desiredDisableValue, {restart: true});
							if (next) {
								setDesktopTroubleshootingState(next);
							}
							setDesktopTroubleshootingBusy(false);
						}}
					/>
				)),
			);
		},
		[t],
	);
	const handleAutostartChange = async (value: boolean) => {
		setAutostartBusy(true);
		const nextState = await setAutostartEnabled(value);
		if (nextState !== null) {
			setAutostartEnabledState(nextState);
		}
		setAutostartBusy(false);
	};
	const handleDesktopWindowBehaviorChange = async (settings: Partial<DesktopWindowBehaviorSettings>) => {
		setDesktopWindowBehaviorBusy(true);
		const nextSettings = await setDesktopWindowBehaviorSettings(settings);
		if (nextSettings !== null) {
			setDesktopWindowBehaviorState(nextSettings);
		}
		const pending = await getDesktopWindowBehaviorPendingRestart();
		setTrayChangePendingRestart(pending);
		setDesktopWindowBehaviorBusy(false);
		return {pending};
	};
	const handleShowTrayIconChange = (value: boolean) => {
		void handleDesktopWindowBehaviorChange({showTrayIcon: value}).then(({pending}) => {
			if (!pending || !isNativeLinux()) return;
			ModalCommands.push(
				modal(() => (
					<ConfirmModal
						title={t`Restart ${PRODUCT_NAME}?`}
						description={
							<Trans>
								Tray icon changes only apply after a restart on Linux because the system tray protocol doesn&rsquo;t
								reliably let apps add or remove icons at runtime. Restart now to apply your change. Any drafts you
								haven&rsquo;t sent will be lost.
							</Trans>
						}
						primaryText={t`Restart Now`}
						primaryVariant="primary"
						secondaryText={t`Later`}
						onPrimary={async () => {
							await relaunchDesktopApp();
						}}
					/>
				)),
			);
		});
	};
	return (
		<SettingsTabContainer>
			<SettingsTabSection
				title={<Trans>Window Focus Behavior</Trans>}
				description={
					<Trans>
						While Fluxer is unfocused, decorative motion (animations, GIFs, typing dots) and hover-only controls (action
						bars, tooltips, link underlines) are paused to save power. You can also choose whether the first click that
						focuses the window should pass through to the control under the pointer.
					</Trans>
				}
			>
				<Switch
					label={<Trans>Stay Fully Interactive When Unfocused</Trans>}
					description={
						<Trans>
							Keep animations, GIF playback, hover effects, and tooltips running even when another app has focus.
						</Trans>
					}
					value={Accessibility.stayInteractiveWhenUnfocused}
					onChange={(value) => Accessibility.updateSettings({stayInteractiveWhenUnfocused: value})}
				/>
				<Switch
					label={<Trans>First Click Pass-Through When Unfocused</Trans>}
					description={
						isNativeMacOS() ? (
							trayChangePendingRestart ? (
								<Trans>
									Let the click that focuses Fluxer also activate the control under the pointer. Restart Fluxer for this
									change to take effect — macOS sets click-through behavior when the window is created.
								</Trans>
							) : (
								<Trans>
									Let the click that focuses Fluxer also activate the control under the pointer. macOS applies this
									setting when the app window is created, so toggling it requires a restart.
								</Trans>
							)
						) : (
							<Trans>
								Let the click that focuses Fluxer also activate the control under the pointer. Leave this off if you
								prefer the first click to only focus the app window.
							</Trans>
						)
					}
					value={Accessibility.firstClickPassThroughWhenUnfocused}
					onChange={(value) => {
						Accessibility.updateSettings({firstClickPassThroughWhenUnfocused: value});
						if (!isDesktop()) return;
						setTimeout(() => {
							void getDesktopWindowBehaviorPendingRestart().then(setTrayChangePendingRestart);
						}, 100);
					}}
				/>
			</SettingsTabSection>
			{isDesktop() && (
				<SettingsTabSection
					title={<Trans>Desktop Startup</Trans>}
					description={
						<Trans>Run {PRODUCT_NAME} automatically when your computer starts. Or don't. Your choice!</Trans>
					}
				>
					<Switch
						label={<Trans>Launch {PRODUCT_NAME} at Login</Trans>}
						description={<Trans>Applies only to the desktop app on this device.</Trans>}
						value={autostartEnabled}
						disabled={autostartBusy}
						onChange={handleAutostartChange}
					/>
				</SettingsTabSection>
			)}
			{isDesktop() && (
				<SettingsTabSection
					title={<Trans>Desktop Window</Trans>}
					description={
						<Trans>
							Choose what {PRODUCT_NAME} remembers about your window between restarts and reloads on this device.
						</Trans>
					}
				>
					{desktopWindowBehavior && (
						<>
							<Switch
								label={<Trans>Remember Size &amp; Position</Trans>}
								description={<Trans>Keep your window dimensions and placement even when you reload the app.</Trans>}
								value={desktopWindowBehavior.rememberWindowState}
								disabled={desktopWindowBehaviorBusy}
								onChange={(value) => handleDesktopWindowBehaviorChange({rememberWindowState: value})}
							/>
							<Switch
								label={<Trans>Use Native Title Bar</Trans>}
								description={
									<Trans>
										Use the operating system&rsquo;s own window chrome instead of Fluxer&rsquo;s custom title bar.
										Applies the next time you reopen the desktop app.
									</Trans>
								}
								value={desktopWindowBehavior.useNativeTitleBar}
								disabled={desktopWindowBehaviorBusy}
								onChange={(value) => handleDesktopWindowBehaviorChange({useNativeTitleBar: value})}
							/>
							<Switch
								label={<Trans>Show Tray Icon</Trans>}
								description={
									trayChangePendingRestart && isNativeLinux() ? (
										<Trans>
											Restart Fluxer for this change to take effect — Linux desktops can&rsquo;t reliably add or remove
											tray icons at runtime.
										</Trans>
									) : (
										<Trans>
											Add a Fluxer icon to the Windows/Linux system tray or the macOS menu bar while the desktop app is
											running. On Linux, changes apply after restarting Fluxer.
										</Trans>
									)
								}
								value={desktopWindowBehavior.showTrayIcon}
								disabled={desktopWindowBehaviorBusy}
								onChange={handleShowTrayIconChange}
							/>
							<Switch
								label={<Trans>Minimize to Tray</Trans>}
								description={
									<Trans>
										Hide the main window when you minimize it, then reopen Fluxer from the tray icon or app menu.
									</Trans>
								}
								value={desktopWindowBehavior.minimizeToTray}
								disabled={desktopWindowBehaviorBusy || !desktopWindowBehavior.showTrayIcon}
								onChange={(value) => handleDesktopWindowBehaviorChange({minimizeToTray: value})}
							/>
							<Switch
								label={<Trans>Close to Tray</Trans>}
								description={
									<Trans>
										Keep Fluxer running when you close the main window. On Linux, this depends on your desktop
										environment having StatusNotifier/AppIndicator tray support.
									</Trans>
								}
								value={desktopWindowBehavior.closeToTray}
								disabled={desktopWindowBehaviorBusy || !desktopWindowBehavior.showTrayIcon}
								onChange={(value) => handleDesktopWindowBehaviorChange({closeToTray: value})}
							/>
						</>
					)}
				</SettingsTabSection>
			)}
			{isDesktop() && (
				<SettingsTabSection
					title={<Trans>Hardware Acceleration</Trans>}
					description={
						<Trans>
							Use the GPU to render the Fluxer window. Disable this only if you&rsquo;re seeing graphics glitches,
							flickering, blank windows, or extreme GPU usage. Toggling this restarts the desktop app.
						</Trans>
					}
				>
					<Switch
						label={<Trans>Use Hardware Acceleration</Trans>}
						description={
							<Trans>
								Off means Chromium falls back to software rendering for the entire app, which is slower but more
								compatible.
							</Trans>
						}
						value={!(desktopTroubleshooting?.disableHardwareAcceleration ?? false)}
						disabled={desktopTroubleshootingBusy || desktopTroubleshooting === null}
						onChange={handleHardwareAccelerationChange}
					/>
				</SettingsTabSection>
			)}
			<SettingsTabSection
				title={<Trans>Developer Options</Trans>}
				description={
					<Trans>
						Enable advanced features for debugging and development. Note that copying snowflake IDs for entities is
						always available to all users without needing developer mode.
					</Trans>
				}
			>
				<Switch
					label={<Trans>Developer Mode</Trans>}
					description={
						<Trans>
							When enabled, reveals debugging menus throughout the app to inspect and copy raw JSON objects of internal
							data structures like messages, channels, users, and communities. Also includes tools to debug the Fluxer
							Markdown parser performance and AST for any given message.
						</Trans>
					}
					value={developerMode}
					onChange={(value) => UserSettingsCommands.update({developerMode: value})}
				/>
			</SettingsTabSection>
		</SettingsTabContainer>
	);
});

export default AdvancedTab;
